/**
 * isProductReservation.js
 *
 * Util central pour distinguer une réservation de listing classique
 * (location, service, immobilier…) d'une vente de produit marketplace.
 *
 * Stratégie : interroger GET /products/:id
 *   → 200 avec données produit  ⇒ c'est un produit  (isProduct = true)
 *   → 404 / erreur              ⇒ c'est un listing   (isProduct = false)
 *
 * Un cache LRU léger évite les appels réseau répétés pour le même UUID.
 */

import api from '../services/api';

// ─── Cache simple (session) ────────────────────────────────────────────────
const _cache = new Map(); // uuid → boolean | 'pending'

/**
 * Vérifie si un identifiant correspond à un produit marketplace.
 *
 * @param {string} itemId  – UUID du listing_id / product_id à tester
 * @returns {Promise<boolean>}
 */
export const isProductReservation = async (itemId) => {
  if (!itemId) return false;

  // Résultat en cache
  if (_cache.has(itemId)) {
    const cached = _cache.get(itemId);
    // Attendre si une requête est en cours pour ce même id
    if (cached instanceof Promise) return cached;
    return cached;
  }

  // Lancer la requête et mettre en cache la promesse pour éviter les doublons concurrents
  const promise = api
    .get(`/products/${itemId}`)
    .then((res) => {
      const data = res.data;
      // L'API peut envelopper dans data.data ou renvoyer directement
      const product = data?.data || data;
      const isProduct = !!(product && (product.id || product.uuid));
      _cache.set(itemId, isProduct);
      return isProduct;
    })
    .catch(() => {
      _cache.set(itemId, false);
      return false;
    });

  _cache.set(itemId, promise);
  return promise;
};

/**
 * Version synchrone — utilise uniquement le cache.
 * Retourne undefined si l'information n'est pas encore disponible.
 *
 * @param {string} itemId
 * @returns {boolean | undefined}
 */
export const isProductReservationSync = (itemId) => {
  if (!itemId) return false;
  const cached = _cache.get(itemId);
  if (cached === undefined || cached instanceof Promise) return undefined;
  return cached;
};

/**
 * Pré-charge le résultat pour un uuid donné (warm-up).
 * @param {string} itemId
 * @returns {Promise<void>}
 */
export const preloadProductCheck = (itemId) => isProductReservation(itemId).then(() => {});

/**
 * Vide le cache (utile pour les tests ou après une modification de produit).
 */
export const clearProductReservationCache = () => _cache.clear();

export default isProductReservation;
